import { ReactComponent as EmployeeIcon } from '@ubeya/shared-web/assets/employee.svg';
import { ReactComponent as BaseDeleteIcon } from '@ubeya/shared-web/assets/delete.svg';
import { ReactComponent as BaseDownloadIcon } from '@ubeya/shared-web/assets/import.svg';
import { ReactComponent as BasePlusIcon } from '@ubeya/shared-web/assets/help-desk/plus.svg';
import { ContextMenu as BaseContextMenu, Button, Flex, PromptClick } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import useChatIframe from '@ubeya/shared-web/hooks/useChatIframe';
import privileges from '@ubeya/shared-web/privileges';
import { media } from '@ubeya/shared-web/style';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useModules from '@ubeya/shared/hooks/account/useModules';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import { saveAs } from 'file-saver';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { getHelpDeskEmployeeTicketsQueryKey } from '@ubeya/shared/hooks/account/useHelpDeskEmployeeTickets';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import DeleteEmployeeModal from 'employees/components/DeleteEmployeeModal';
import DeactivateEmployeeModal from 'employees/components/DeactivateEmployeeModal';
import CreateTicketModal from 'helpDesk/components/CreateTicket/CreateTicketModal';
import { useHelpDeskAnalytics } from 'helpDesk/helpDeskAnalytics';
import useOpenTicketInSidePanel from 'helpDesk/useOpenTicketInSidePanel';

const ActivateIcon = styled(EmployeeIcon)`
  path {
    fill: ${({ theme }) => theme.colors.greenPositive};
    stroke: transparent;
  }
`;

const InActivateIcon = styled(EmployeeIcon)`
  path {
    fill: ${({ theme }) => theme.colors.grey4};
    stroke: transparent;
  }
`;

const DeleteIcon = styled(BaseDeleteIcon)`
  width: 17px;
  height: 17px;
`;

const ImportIcon = styled(BaseDownloadIcon)`
  width: 17px;
  height: 17px;
`;

const CreateTicketIcon = styled(BasePlusIcon)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.grey4};
`;

const ContextMenu = styled(BaseContextMenu)`
  > div {
    > div {
      height: 32px;
    }
  }
  height: fit-content;

  > div {
    > div {
      margin-left: 8px;
      background-color: transparent;
      border-radius: 4px;
      color: ${({ theme }) => theme.colors.black};
      min-width: 32px;

      &:hover {
        background-color: transparent;
      }
    }
  }
`;

const ActionsContainer = styled(Flex)`
  ${media.tablet`justify-content: space-between;width: 100%;`};
`;

const ButtonsContainer = styled(Flex)`
  gap: 8px;
  width: 100%;
  align-items: center;
  margin-left: 20px;
`;

const ActionButton = styled(Button)`
  border: solid 1px ${({ theme, $isDisabled }) => ($isDisabled ? theme.colors.disabled : theme.colors.grey3)};

  background-color: ${({ theme, $isDisabled }) => ($isDisabled ? theme.colors.disabled : 'transparent')};
  color: ${({ theme, $isDisabled }) => ($isDisabled ? theme.colors.grey3 : theme.colors.black)};
  border-radius: 4px;
  display: flex;
  min-height: 24px;
  max-height: 24px;
  justify-content: center;
  padding: 0px 8px !important;

  ${media.tablet`padding: 0 12px;`}
`;

const Actions = ({ mode, handleClose, isDeleted, employeeId, isActive, inviteEmployee, employeeFields, id }) => {
  const { t, isAccountNetworkAccessSupplier } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const queryClient = useQueryClient();
  const { modules } = useModules();
  const { trackCreateTicketOpened } = useHelpDeskAnalytics();
  const myPrivileges = useContext(PrivilegesContext);
  const {
    input: { value: firstName }
  } = useField('firstName');

  const {
    input: { value: lastName }
  } = useField('lastName');

  const {
    input: { value: image }
  } = useField('image');

  const { openUserChat, hasViewChatPrivilege } = useChatIframe();

  const [deleteEmployeeModal, setDeleteEmployeeModal] = useState(false);
  const [deactivateEmployeeModal, setDeactivateEmployeeModal] = useState(false);
  const [createTicketModal, setCreateTicketModal] = useState(false);

  const onCloseCreateTicketModal = useCallback(() => setCreateTicketModal(false), []);

  const onOpenCreateTicket = useCallback(() => {
    trackCreateTicketOpened('Employee profile - actions menu');
    setCreateTicketModal(true);
  }, [trackCreateTicketOpened]);

  const openTicketInSidePanel = useOpenTicketInSidePanel();
  const onTicketCreated = useCallback(
    (ticket) => {
      queryClient.invalidateQueries(getHelpDeskEmployeeTicketsQueryKey(accountId, employeeId));
      setCreateTicketModal(false);
      openTicketInSidePanel(ticket?.id);
    },
    [queryClient, accountId, employeeId, openTicketInSidePanel]
  );

  const { updateEmployee } = useEmployee({ employeeId, enabled: false });

  const onRestoreEmployee = useCallback(async () => {
    await updateEmployee({ accountId, employeeId, isDeleted: false, invalidateEmployee: false });
    handleClose();
  }, [accountId, employeeId, handleClose, updateEmployee]);
  const contextMenuOptions = useMemo(
    () => [
      {
        visible: !isDeleted && isActive && !!modules?.help_desk,
        label: t('help_desk_create_ticket'),
        icon: CreateTicketIcon,
        onClick: onOpenCreateTicket
      },
      {
        visible: !isDeleted && !!myPrivileges[privileges.EDIT_HR],
        icon: isActive ? ActivateIcon : InActivateIcon,
        label: t(isActive ? 'turnToNonActive' : 'turnToActive'),
        onClick: () => {
          if (isActive) {
            return setDeactivateEmployeeModal({
              employee: {
                firstName,
                lastName,
                id
              },
              updateEmployee
            });
          }

          updateEmployee({ accountId, employeeId, isActive: !isActive, invalidateEmployee: false });
          handleClose();
        }
      },
      {
        visible: !!myPrivileges[privileges.DELETE_EMPLOYEE],
        label: isDeleted ? t('restoreEmployee') : t('deleteEmployee'),
        icon: DeleteIcon,
        onClick: () => (isDeleted ? onRestoreEmployee() : setDeleteEmployeeModal(true))
      },
      {
        visible: image && !!myPrivileges[privileges.ACCOUNT_ADMIN],
        label: t('downloadProfilePicture'),
        icon: ImportIcon,
        onClick: () => saveAs(image, `${firstName}-${lastName}.png`)
      }
    ],
    [
      t,
      firstName,
      myPrivileges,
      isDeleted,
      isActive,
      updateEmployee,
      accountId,
      employeeId,
      handleClose,
      lastName,
      id,
      onRestoreEmployee,
      image,
      modules,
      onOpenCreateTicket
    ]
  );

  return (
    <>
      {mode !== 'ADD' && (
        <ActionsContainer>
          <ButtonsContainer>
            {hasViewChatPrivilege && (
              <ActionButton
                disabled={isAccountNetworkAccessSupplier || !id}
                tooltip={isAccountNetworkAccessSupplier ? t('disabledDirectWorkerChat') : t('chat')}
                $isDisabled={isAccountNetworkAccessSupplier || !id}
                type="button"
                onClick={() => {
                  handleClose();
                  openUserChat(id, accountId);
                }}>
                <span>{t('chat')}</span>
              </ActionButton>
            )}
            <PromptClick
              body={t('areYouSureSendSMSInvite')}
              confirmText="send"
              stopPropagation
              onClick={() => inviteEmployee()}>
              <ActionButton>
                <span>{t('invite')}</span>
              </ActionButton>
            </PromptClick>
          </ButtonsContainer>
          <ContextMenu options={contextMenuOptions} usePortal />
        </ActionsContainer>
      )}
      {createTicketModal && (
        <CreateTicketModal
          lockedEmployeeId={employeeId}
          onClose={onCloseCreateTicketModal}
          onCreated={onTicketCreated}
        />
      )}

      {deleteEmployeeModal && (
        <DeleteEmployeeModal
          employee={{
            id: employeeId,
            firstName,
            lastName
          }}
          employeeFields={employeeFields}
          updateEmployee={updateEmployee}
          onClose={() => {
            setDeleteEmployeeModal(false);
            handleClose();
          }}
        />
      )}

      {deactivateEmployeeModal && (
        <DeactivateEmployeeModal
          employee={{
            id: employeeId,
            firstName,
            lastName
          }}
          updateEmployee={updateEmployee}
          onClose={() => {
            setDeactivateEmployeeModal(false);
            handleClose();
          }}
        />
      )}
    </>
  );
};

export default Actions;
